/* eslint-disable camelcase */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';

import Loader from '../../../../elements/Loader';
import FormSwitchSelector from '../../../../elements/formElements/FormSwitchSelector';
import SubmitButton from '../../../../elements/formElements/SubmitButton';
import axiosCRM from '../../../../services/MyAxios';

import { CASINO_SETTINGS } from '../../Helpers/CrmUrls';

import CountOfGamesPerRow from './CountOfGamesPerRow';

interface Settings {
    games_per_row_count: string;
    show_demo_button: string;
    show_game_miniature_details: string;
}

function GamesSetting() {
    const url = '/crea/casino/api/settings/games';
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<Settings>({
        games_per_row_count: '',
        show_demo_button: '',
        show_game_miniature_details: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = () => {
        axiosCRM
            .post(`${url}/update`, settings)
            .then((response) => {
                if (response.status === 204) {
                    swal({
                        title: 'Success',
                        text: 'Game settings updated correctly',
                        icon: 'success',
                    }).then(() => {
                        history.push(CASINO_SETTINGS);
                    });
                }
            })
            .catch((error) => {
                if (!error.response) {
                    setErrors({ message: error.message });
                } else if (error.response.status !== 500) {
                    setErrors(error.response.data.errors);
                } else {
                    swal({
                        title: 'Error',
                        text: 'Server error',
                        icon: 'error',
                    });
                }
            });
    };

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const { value, name } = event.target;
        setSettings({ ...settings, [name]: value });
    };

    const handleSwitchSelectorChange = (value: string) => {
        setSettings({ ...settings, show_demo_button: value });
    };

    const handleShowGameMiniatureDetailsChange = (value: string) => {
        setSettings({ ...settings, show_game_miniature_details: value });
    };

    const getCurrentSettings = () => {
        axiosCRM
            .get(url)
            .then((response) => {
                setSettings(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'Server error',
                    icon: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getCurrentSettings();
    }, []);

    const options = [
        {
            label: 'Hide',
            value: '0',
            selectedBackgroundColor: '#de1a24',
            innerHeight: 50,
        },
        {
            label: 'Show',
            value: '1',
            selectedBackgroundColor: '#3f8f29',
        },
    ];

    const getContent = () => {
        return (
            <div className="form-flex">
                <CountOfGamesPerRow
                    currentValue={parseInt(settings.games_per_row_count)}
                    handleChange={handleChange}
                    error={errors.games_per_row_count ?? undefined}
                />
                <FormSwitchSelector
                    label="Show play demo button"
                    handleChange={handleSwitchSelectorChange}
                    options={options}
                    currentValue={settings.show_demo_button}
                    required
                    field="show_demo_button"
                    error={errors.show_demo_button ?? undefined}
                />
                <FormSwitchSelector
                    label="Show game details"
                    handleChange={handleShowGameMiniatureDetailsChange}
                    options={options}
                    currentValue={settings.show_game_miniature_details}
                    required
                    field="show_game_miniature_details"
                    error={errors.show_game_miniature_details ?? undefined}
                />
                <SubmitButton isLoading={isLoading} onClick={handleSubmit} label="Save" />
            </div>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}

export default GamesSetting;
